import { Injectable } from '@angular/core';
import { catchError, firstValueFrom, from, Observable, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import { CookieWrapper } from './cookie.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { AdminUserModel } from '@models/admin-user.model';
import { Civility } from '@models/civility.model';
import { HttpService } from './http.service';
import { UpdatePasswordRequest } from '@models/update-password.model';
import { TokenRefreshService } from './token-refresh.service';

@Injectable({
  providedIn: 'root',
})
export class GestionLinker {
  private readonly usersApi = `${environment.portageUrl}/v2/users`;

  constructor(
    private httpService: HttpService,
    private cookieService: CookieWrapper,
    private toastr: ToastrService,
    private router: Router,
    private tokenRefreshService: TokenRefreshService
  ) {}

  login(email: string, password: string): Observable<any> {
    const requestBody = {
      email: email,
      password: password,
    };

    return this.#post('/auth/login', requestBody, false);
  }

  getUserById(id: number): Observable<any> {
    return this.#get(`/v2/users/${id}`);
  }

  /** ---[ Start Users ]--- */
  getUsers(): Observable<AdminUserModel[]> {
    return this.#get('/v2/users');
  }

  updateUser(
    userData: Partial<{
      id: number | null;
      firstname: string | null;
      lastname: string | null;
      email: string | null;
      username: string | null;
      roles: string[] | null;
    }>
  ): Observable<any> {
    return this.httpService.put(`${this.usersApi}`, userData);
  }

  toggleUser(userId: number, enable: boolean): Observable<any> {
    const status = enable ? 'enable' : 'disable';
    return this.httpService.patch(`${this.usersApi}/${userId}/${status}`);
  }

  resetPasswordForUser(userId: number): Observable<any> {
    return this.#patch(`/portage/users/${userId}/reset-password`, {});
  }

  resetPasswordForMyself(): Observable<any> {
    return this.#patch(`/portage/users/me/reset-password`, {});
  }

  createUser(userData: Partial<AdminUserModel>): Observable<any> {
    return this.httpService.post(`${this.usersApi}`, userData);
  }

  check(
    type: 'email' | 'username',
    value: string,
    userId?: number
  ): Observable<any> {
    const params: Record<string, string | number> = {
      [type]: value,
    };
    if (userId !== undefined) {
      params['userid'] = userId;
    }
    return this.httpService.get(`${this.usersApi}/check`, params);
  }

  /** ---[ End Users ]--- */
  /** ---[ Start Civilities ]--- */

  getCivilities(): Observable<Civility[]> {
    return this.#get('/portage/referentiel/civilites');
  }

  createCivility(civilityData: Partial<Civility>): Observable<any> {
    return this.#post('/portage/referentiel/civilites', civilityData);
  }

  updateCivility(
    civilityId: number,
    civilityData: Partial<Civility>
  ): Observable<any> {
    return this.#patch(
      `/portage/referentiel/civilites/${civilityId}`,
      civilityData
    );
  }

  disableCivility(civilityId: number): Observable<any> {
    return this.#delete(`/portage/referentiel/civilites/${civilityId}/disable`);
  }

  enableCivility(civilityId: number): Observable<any> {
    return this.#patch(
      `/portage/referentiel/civilites/${civilityId}/enable`,
      {}
    );
  }

  async #fetchWithRefresh(
    url: string,
    options: RequestInit
  ): Promise<Response> {
    const response = await fetch(url, options);
    if (response.status !== 401) {
      return response;
    }

    try {
      const newToken = await firstValueFrom(
        this.tokenRefreshService.refreshAccessToken()
      );
      const retryHeaders = {
        ...(options.headers as Record<string, string>),
        Authorization: `Bearer ${newToken}`,
      };
      return fetch(url, { ...options, headers: retryHeaders });
    } catch {
      this.handleUnauthorized();
      throw new Error('Session expirée');
    }
  }

  #post(
    endpoint: string,
    body: any,
    requiresAuth: boolean = true
  ): Observable<any> {
    const url = `${environment.portageUrl}${endpoint}`;
    const headers = requiresAuth
      ? this.getAuthHeaders()
      : { 'Content-Type': 'application/json', Accept: 'application/json' };
    const options: RequestInit = {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    };

    const request = requiresAuth
      ? this.#fetchWithRefresh(url, options)
      : fetch(url, options);

    return from(
      request.then(async response => {
        if (response.status === 401 && requiresAuth) {
          this.handleUnauthorized();
          throw new Error('Session expirée');
        }
        const data = await response.json();
        if (data.code !== undefined && data.message !== undefined) {
          throw new Error(data.message);
        }
        return data;
      })
    ).pipe(catchError(err => throwError(() => err.message || err)));
  }

  #patch(
    endpoint: string,
    body: any,
    requiresAuth: boolean = true
  ): Observable<any> {
    const url = `${environment.portageUrl}${endpoint}`;
    const headers = requiresAuth
      ? this.getAuthHeaders()
      : { 'Content-Type': 'application/json', Accept: 'application/json' };
    const options: RequestInit = {
      method: 'PATCH',
      headers,
      body: JSON.stringify(body),
    };

    const request = requiresAuth
      ? this.#fetchWithRefresh(url, options)
      : fetch(url, options);

    return from(
      request.then(async response => {
        if (response.status === 401 && requiresAuth) {
          this.handleUnauthorized();
          throw new Error('Session expirée');
        }
        return response.json();
      })
    ).pipe(catchError(err => throwError(() => err.message || err)));
  }

  #get(endpoint: string, requiresAuth: boolean = true): Observable<any> {
    const url = `${environment.portageUrl}${endpoint}`;
    const headers = requiresAuth
      ? this.getAuthHeaders()
      : { 'Content-Type': 'application/json', Accept: 'application/json' };

    const request = requiresAuth
      ? this.#fetchWithRefresh(url, { method: 'GET', headers })
      : fetch(url, { method: 'GET', headers });

    return from(
      request.then(async response => {
        if (response.status === 401 && requiresAuth) {
          this.handleUnauthorized();
          throw new Error('Session expirée');
        }
        const data = await response.json();
        if (data.code !== undefined && data.message !== undefined) {
          throw new Error(data.message);
        }
        return data;
      })
    ).pipe(catchError(err => throwError(() => err.message || err)));
  }

  #delete(endpoint: string, requiresAuth: boolean = true): Observable<any> {
    const headers = requiresAuth
      ? this.getAuthHeaders()
      : {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        };

    return from(
      fetch(`${environment.boGestionUrl}${endpoint}`, {
        method: 'DELETE',
        headers: headers,
      })
        .then(response => response.json())
        .catch(error => {
          console.log(error);
        })
    ).pipe(catchError(err => throwError(() => err.message || err)));
  }

  private getAuthHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    // Récupération du token Bearer depuis la session
    if (this.cookieService.check('portage_token')) {
      const token = this.cookieService.get('portage_token');
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private handleUnauthorized(): void {
    this.tokenRefreshService.clearTokens();
    this.toastr.error(
      'Votre session a expiré. Veuillez vous reconnecter.',
      'Session expirée'
    );
    void this.router.navigate(['/login']);
  }

  updatePassword(data: UpdatePasswordRequest): Observable<any> {
    return this.httpService.put(`${this.usersApi}/reset-password`, data);
  }
}
